import { useEffect, useState } from 'react';
import {
  audienceOutcomes,
  biography,
  publications,
  signatureTalks,
  testimonial
} from '../../content/siteContent';
import { getPublicEngagements } from '../../lib/api';
import type { Engagement } from '../../lib/types';
import { AnimatedSection } from '../components/AnimatedSection';
import { BookingCta } from '../components/BookingCta';
import { BookCover } from '../components/BookCover';
import { EngagementList } from '../components/EngagementList';

const homepagePublications = [...publications].reverse();

export function HomePage() {
  const [engagements, setEngagements] = useState<Engagement[]>([]);

  useEffect(() => {
    let isCurrent = true;

    getPublicEngagements()
      .then((publishedEngagements) => {
        if (isCurrent) setEngagements(publishedEngagements.slice(0, 2));
      })
      .catch(() => undefined);

    return () => {
      isCurrent = false;
    };
  }, []);

  return (
    <>
      <section className="hero">
        <div className="hero__copy">
          <p className="eyebrow">Ancient intelligence · Modern wellbeing</p>
          <h1 className="hero__name" aria-label="Christina J. Barea">
            <span>Christina J.</span>
            <span>Barea</span>
          </h1>
          <p className="hero__lead">Movement is life.</p>
          <p className="hero__roles">
            Qigong Instructor &amp; Clinician · Certified Health Professional · Coach · Author · Speaker
          </p>
          <div className="hero__actions">
            <a className="button" href="/contact">Book Christina</a>
            <a className="text-link" href="/speaking">Explore speaking topics</a>
          </div>
        </div>
        <div className="hero__portrait">
          <div className="hero__orb" aria-hidden="true" />
          <img src="/images/christina-hero.png" alt="Christina J Barea" />
          <p>23 years of practice, teaching, and clinical insight</p>
        </div>
      </section>

      <div className="proof-strip" aria-label="Professional highlights">
        <span>Award-winning author</span>
        <span>Medical Qigong clinician</span>
        <span>Certified Coach</span>
        <span>Television host</span>
        <span>English · Spanish · Italian</span>
      </div>

      <AnimatedSection className="intro-section">
        <p className="eyebrow">Where science meets tradition</p>
        <div className="intro-section__grid">
          <h2>Practical wisdom for a body built to adapt.</h2>
          <div>
            <p>{biography[0]}</p>
            <a className="text-link" href="/about">Meet Christina</a>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="featured-talks" aria-labelledby="featured-talks-title">
        <div className="section-heading">
          <div><p className="eyebrow">Signature talks</p><h2 id="featured-talks-title">Ideas audiences can feel—and use.</h2></div>
          <a className="text-link" href="/speaking">View all speaking topics</a>
        </div>
        <div className="talk-grid">
          {signatureTalks.slice(0, 3).map((talk, index) => (
            <article className="talk-card" key={talk.title}>
              <span>0{index + 1}</span>
              <h3>{talk.title}</h3>
              <p>{talk.shortTitle}</p>
            </article>
          ))}
        </div>
      </AnimatedSection>

      <AnimatedSection className="outcomes-section">
        <div className="outcomes-section__statement">
          <p className="eyebrow">What stays with the audience</p>
          <h2>Less overwhelm. More agency.</h2>
        </div>
        <ol className="outcome-list">
          {audienceOutcomes.map((outcome, index) => <li key={outcome}><span>0{index + 1}</span>{outcome}</li>)}
        </ol>
      </AnimatedSection>

      <AnimatedSection className="quote-section">
        <blockquote>“{testimonial.quote}”</blockquote>
      </AnimatedSection>

      {engagements.length > 0 && (
        <AnimatedSection className="engagement-preview" aria-labelledby="engagement-preview-title">
          <div className="section-heading">
            <div>
              <p className="eyebrow">On the calendar</p>
              <h2 id="engagement-preview-title">Upcoming engagements</h2>
            </div>
            <a className="text-link" href="/engagements">View all engagements</a>
          </div>
          <EngagementList engagements={engagements} />
        </AnimatedSection>
      )}

      <AnimatedSection className="books-preview">
        <div>
          <p className="eyebrow">Books & media</p>
          <h2>Frameworks made visible.</h2>
          <p>{biography[1]}</p>
          <a className="text-link" href="/books-media">Explore the work</a>
        </div>
        <div className="book-stack" aria-label="Publications">
          {homepagePublications.map((publication, index) => (
            <BookCover
              key={publication.title}
              publication={publication}
              containerClassName={`book-stack__item book-stack__item--${index + 1}`}
              imageClassName="book-stack__cover"
            />
          ))}
        </div>
      </AnimatedSection>

      <BookingCta />
    </>
  );
}
